import { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { Workspace } from '../../mainApi';
import { MainFilter } from '../../stores/mainStore';

type MainSidebarProps = {
  drawerWidth: number;
  filter: MainFilter;
  selectedWorkspaceId: string | null;
  workspaces: Workspace[];
  homeHref: string;
  onSelectHome: () => void;
  onSelectTemplates: () => void;
  onSelectArchived: () => void;
  onSelectWorkspace: (workspaceId: string) => void;
  onCreateWorkspace: () => void;
};

export const MainSidebar = ({
  drawerWidth,
  filter,
  selectedWorkspaceId,
  workspaces,
  homeHref,
  onSelectHome,
  onSelectTemplates,
  onSelectArchived,
  onSelectWorkspace,
  onCreateWorkspace,
}: MainSidebarProps) => {
  const [workspacesExpanded, setWorkspacesExpanded] = useState(true);
  const ownedWorkspaces = workspaces.filter((workspace) => workspace.isOwner);
  const notOwnedWorkspaces = workspaces.filter((workspace) => !workspace.isOwner);
  const orderedWorkspaces = [...ownedWorkspaces, ...notOwnedWorkspaces];

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        flexShrink: { sm: 0 },
        bgcolor: '#fafbfc',
        position: 'relative',
      }}
    >
      <Toolbar sx={{ justifyContent: 'center', height: '90px', px: '50px' }}>
        <a href={homeHref}>
          <Box
            component="img"
            src="https://storage.googleapis.com/dotstorming-img/main-logo.svg"
            alt="dotstorming"
            sx={{ height: 55, maxWidth: '100%', objectFit: 'contain' }}
          />
        </a>
      </Toolbar>
      <Stack
        spacing={0}
        sx={{ pt: '50px', pb: '25px', height: 'calc(100% - 90px)', overflowY: 'auto' }}
      >
        <List disablePadding>
          <ListItemButton
            selected={filter === 'home'}
            onClick={onSelectHome}
            sx={{ px: '50px', py: '10px', gap: 3 }}
          >
            <Box
              component="img"
              src="https://storage.googleapis.com/dotstorming-img/main_home.svg"
              alt="Home"
              sx={{ width: 25, height: 25 }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Home</Typography>
          </ListItemButton>
          <ListItemButton
            selected={filter === 'templates'}
            onClick={onSelectTemplates}
            sx={{ px: '50px', py: '10px', gap: 3 }}
          >
            <Box
              component="img"
              src="https://storage.googleapis.com/dotstorming-img/main_templates.svg"
              alt="Templates"
              sx={{ height: 26, width: 'auto' }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Templates</Typography>
          </ListItemButton>
        </List>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ pl: '21px', pr: '25px' }}
        >
          <ListItemButton
            disableRipple
            onClick={() => setWorkspacesExpanded((value) => !value)}
            sx={{
              px: 0,
              py: '10px',
              minHeight: 0,
              gap: 1,
              flexGrow: 1,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <ExpandMoreOutlinedIcon
              sx={{
                color: '#a9afb8',
                fontSize: 20,
                transform: workspacesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
            <Stack direction="row" alignItems="center" spacing={3}>
              <Box
                component="img"
                src="https://storage.googleapis.com/dotstorming-img/main_workspaces.svg"
                alt="Workspaces"
                sx={{ height: 25, width: 'auto' }}
              />

              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Workspaces</Typography>
            </Stack>
          </ListItemButton>
          <IconButton size="small" onClick={onCreateWorkspace} sx={{ p: 0.5 }}>
            <Box
              component="img"
              src="https://storage.googleapis.com/dotstorming-img/add-workspace.svg"
              alt="Add Workspace"
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        </Stack>
        <Collapse in={workspacesExpanded} timeout="auto" unmountOnExit>
          {orderedWorkspaces.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: '100px', py: '10px' }}>
              No workspaces
            </Typography>
          ) : (
            <List disablePadding>
              {orderedWorkspaces.map((workspace) => (
                <ListItemButton
                  key={workspace.id}
                  selected={filter === 'workspace' && selectedWorkspaceId === workspace.id}
                  onClick={() => onSelectWorkspace(workspace.id ?? '')}
                  sx={{ pl: '100px', pr: '25px', py: 1 }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                    {workspace.title || 'Untitled workspace'}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          )}
        </Collapse>
      </Stack>
    </Box>
  );
};
